import { Component, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { ColumnFilterService } from './column-filter/column-filter.service';
import { ContactAndSupportDialogComponent } from './contact-and-support-dialog/contact-and-support-dialog.component';
import {
  ExperimentDialogComponent,
  ExperimentDialogData,
  ExperimentDialogResult,
} from './experiments/experiment-dialog/experiment-dialog.component';
import {
  ICExperimentDialogComponent,
  ICExperimentDialogData,
  ICExperimentDialogResult,
} from './image-capture/experiment-dialog/ic-experiment-dialog.component';
import { ICRoundService } from './image-capture/ic-round.service';
import { CanEditExperimentPipe } from './pipes/can-edit-experiment.pipe';
import { ExperimentFormatPipe } from './pipes/experiment-format.pipe';
import { ReleaseNotesDialogComponent } from './release-notes-dialog/release-notes-dialog.component';
import { AppStateService } from './services/app-state.service';
import { AuditService } from './services/audit.service';
import { AuditObjectName } from './services/audit.service.models';
import { ConfigurationService } from './services/configuration.service';
import { FeatureFlags } from './services/configuration.service.model';
import { LabpartnerService } from './services/labpartner.service';
import {
  Experiment,
  ExperimentSource,
  ICExperiment,
  NotesCountMap,
  User,
} from './services/labpartner.service.model';
import {
  EVENT_CLONE_EXPERIMENT,
  EVENT_USER_NO_ACCESS,
  EVENT_USER_VERSION,
} from './services/logging-constants';
import { LoggingService } from './services/logging.service';
import { NotesService } from './services/notes-service.service';
import { TestLoginService } from './services/test-login.service';
import { TestUserCatalogService } from './services/test-user-catalog.service';
import { UserAccountService } from './services/user-account.service';
import { DialogService } from './shared/dialog.service';
import { NotificationService } from './shared/notification.service';
import { BaseComponent } from './support/base.component';
import { TestLoginDialogComponent } from './test-login-dialog/test-login-dialog.component';

export class ApplicationType {
  static LabPartner = 'Lab Partner';
  static ResultsBundler = 'Results Bundler';
  static SavannaResults = 'Savanna Results';
  static ImageCapture = 'Image Capture';
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent extends BaseComponent implements OnInit {
  isExpanded = new Set<number>();

  title = 'Lab Partner Web';
  isIframe = false;
  loginDisplay = false;
  displayName = '';
  isProduction = false;
  environmentName = '';
  appVersion = '0.0.0.0';
  isTitlebarToolTipDisabled = false;

  public titleBarName: string = '';
  public appType: string = '';
  public environment = environment;

  private readonly _destroying$ = new Subject<void>();
  currentExperiment!: Experiment;
  currentRoles: string[] = [];
  currentUser?: User;

  experimentAuditCount?: number;
  experimentNoteCounts?: NotesCountMap;

  viewingUnauthenticatedRoute: boolean = false;

  ExperimentSource = ExperimentSource;

  featureFlags$: Observable<FeatureFlags | null>;

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    public accountService: UserAccountService,
    public appState: AppStateService,
    public apiService: LabpartnerService,
    public notesService: NotesService,
    private router: Router,
    private columnFilterService: ColumnFilterService,
    private loggingService: LoggingService,
    private configService: ConfigurationService,
    private auditService: AuditService,
    private matDialogService: MatDialog,
    private dialogService: DialogService,
    private experimentPipe: ExperimentFormatPipe,
    private canEditExperimentPipe: CanEditExperimentPipe,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private notificationService: NotificationService,
    public icRoundService: ICRoundService,
    private testUserCatalog: TestUserCatalogService,
    private testLoginService: TestLoginService,
  ) {
    super();
    this.featureFlags$ = this.configService.featureFlags$;
    this.matIconRegistry.addSvgIcon(
      'xlsx',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/excel.svg'),
    );
  }

  protected ngOnDestroyInternal(): void {
    throw new Error('Method not implemented.');
  }

  ngOnInit(): void {
    console.log(`app version ${environment.version}`);

    this.isIframe = window !== window.parent && !window.opener;

    // Fix for Angular 20: Prevent empty window.open calls that cause blank tabs
    const originalOpen = window.open;
    window.open = function (url?: string | URL, target?: string, features?: string) {
      // Block empty URLs with _blank target that cause blank tabs
      if (url === '' && target === '_blank') {
        console.warn('Blocked empty window.open call that would cause blank tab');
        return null;
      }

      return originalOpen.call(this, url, target, features);
    };

    this.environmentName = this.configService.getConfig().environmentName;
    this.isProduction = this.environmentName.toLowerCase() === 'prod';
    this.appVersion = environment.version;

    // In test mode, don't show dialog automatically - let user navigate and login when needed

    this.router.events.subscribe(e => {
      //console.log(`ROUTER: ${e}`);
      if (
        e instanceof NavigationEnd &&
        e.url.startsWith('/mobile-barcodes') &&
        e.url.match(/\/mobile-barcodes\/[0-9]+\/[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}/)
      ) {
        this.viewingUnauthenticatedRoute = true;
      }

      if (!this.router.url.includes('/experiment-detail/')) {
        this.appState.ClearCurrentExperiment();
      }
      this.setAppNames();
    });

    this.accountService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });

    this.accountService.currentRoles$.subscribe(roles => {
      this.currentRoles = roles;
    });

    // Handle MSAL in normal mode
    if (!environment.testMode) {
      this.msalBroadcastService.inProgress$
        .pipe(
          filter(
            (status: InteractionStatus) =>
              status === InteractionStatus.None || status == InteractionStatus.Startup,
          ),
          takeUntil(this._destroying$),
        )
        .subscribe((status: InteractionStatus) => {
          console.log(`msal.inProgress... ${status}`);
          this.setLoggedInUser();
        });
    } else {
      // In test mode, check for test user login with a small delay
      console.log('[AppComponent] Running in test mode - checking for test user');
      setTimeout(() => {
        console.log('[AppComponent] About to call checkTestUserLogin()');
        this.checkTestUserLogin();
      }, 1000); // Increased delay to ensure UserAccountService is ready
    }

    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS))
      .subscribe(() => {
        console.log('msal.msalSubject LOGIN SUCCESS');
        this.setLoggedInUser();
      });

    this.appState.ExperimentOpened.pipe(takeUntil(this._destroying$)).subscribe(
      async (exp: Experiment) => {
        this.currentExperiment = exp;

        this.columnFilterService.resetTableFilter('detail');

        // If we have a current experiment, we are likely in the experiment details area
        // Get granular note counts for that experiment
        if (this.currentExperiment) {
          await this.auditService.getAuditCounts(this.currentExperiment.experimentId).toPromise();
          this.apiService
            .getNoteCountsByExperimentId(this.currentExperiment.experimentId)
            .subscribe(experimentNoteCounts => {
              this.notesService.experimentNotesCountMap.next(experimentNoteCounts);
              this.experimentNoteCounts = experimentNoteCounts.Experiment;
            });
        }
      },
    );

    this.appState.ExperimentDataChanged.pipe(takeUntil(this._destroying$)).subscribe(
      async (expId: number) => {
        await this.auditService.getAuditCounts(expId).toPromise();

        if (this.currentExperiment) {
          this.apiService
            .getNoteCountsByExperimentId(this.currentExperiment.experimentId)
            .subscribe(experimentNoteCounts => {
              this.notesService.experimentNotesCountMap.next(experimentNoteCounts);
              this.experimentNoteCounts = experimentNoteCounts.Experiment;
            });
          this.apiService
            .getExperiment(this.currentExperiment.experimentId)
            .subscribe((experiment: Experiment) => {
              this.appState.SetCurrentExperiment(experiment);
            });
        }
      },
    );

    this.auditService.auditCountMap.pipe(takeUntil(this._destroying$)).subscribe(acm => {
      if (this.appState.GetCurrentExperiment()) {
        const experimentId = this.appState.GetCurrentExperiment().experimentId;
        this.experimentAuditCount = acm.Experiment && acm.Experiment[experimentId];
      }
    });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    console.log('destroying');
  }

  setAppNames(): void {
    const path = window.location.pathname;

    if (path.startsWith('/bundles')) {
      this.titleBarName = 'Results Bundler';
      this.appType = ApplicationType.ResultsBundler;
      this.isTitlebarToolTipDisabled = true;
    } else if (path.startsWith('/savanna-results')) {
      this.titleBarName = 'Savanna Results';
      this.appType = ApplicationType.SavannaResults;
      this.isTitlebarToolTipDisabled = true;
    } else if (path.startsWith('/image-capture')) {
      this.titleBarName = 'Image Capture';
      this.appType = ApplicationType.ImageCapture;
      this.isTitlebarToolTipDisabled = true;
    } else {
      this.titleBarName = 'Lab Partner';
      this.appType = ApplicationType.LabPartner;
      this.isTitlebarToolTipDisabled = false;
    }
  }

  login(): void {
    if (environment.testMode) {
      // In test mode, show the test login dialog
      console.log('[AppComponent] Showing test login dialog');
      this.showTestLoginDialog();
    } else {
      // Normal MSAL login
      this.authService.loginRedirect();
    }
  }

  logout() {
    if (environment.testMode) {
      // Reload the page to reset the application state
      window.location.href = '/noaccess';
      // In test mode, clear localStorage and reload page
      console.log('[AppComponent] Logging out test user');
      localStorage.clear();
      this.loginDisplay = false;
      this.displayName = '';
      this.accountService.clearUser();
    } else {
      // Normal MSAL logout
      this.authService.logoutRedirect();
      this.accountService.clearUser();
    }
  }

  removeAdmin() {
    this.accountService.removeAdmin();
  }

  removeWriter() {
    this.accountService.removeWriter();
  }

  removeReader() {
    this.accountService.removeReader();
  }

  removeICWriter() {
    this.accountService.removeICWriter();
  }

  checkTestUserLogin() {
    // In test mode, start without any user logged in
    // Users will login through the UI (controlled by Cypress/Playwright)
    if (environment.testMode) {
      // Check if user is already logged in
      if (this.loginDisplay && this.displayName) {
        return;
      }

      // Check if we're already on a protected route (not /noaccess)
      if (!this.router.url.includes('/noaccess')) {
        return;
      }

      this.loginDisplay = false; // No user logged in initially
      this.displayName = '';

      // Clear any existing user data
      this.accountService.clearUser();

      // In test mode, redirect to noaccess page to force login
      this.router.navigate(['/noaccess']);
    } else {
      this.loginDisplay = false;
    }
  }

  public setTestUserInAccountService(testUserType: string) {
    // Clear the UserAccountService before setting new user
    this.accountService.clearUser();

    // Define test users with proper role mapping
    const testUsers: { [key: string]: any } = {
      admin: {
        oid: 'd60d888a-5bcb-4d3d-b081-401c0dbfdedd',
        name: 'Automation Admin',
        email: 'admin@quidelortho.com',
        roles: [
          'LabPartner.Administrators',
          'LabPartner.Writers',
          'LabPartner.Readers',
          'ResultsBundler.ClinicalTrials',
          'LabPartner.Viewers',
        ],
      },
      writer: {
        oid: 'a60d888a-5bcb-4d3d-b081-401c0dbfdedd',
        name: 'Automation Writer',
        email: 'writer@quidelortho.com',
        roles: ['LabPartner.Writers', 'LabPartner.Readers', 'LabPartner.Viewers'],
      },
      reader: {
        oid: 'b60d888a-5bcb-4d3d-b081-401c0dbfdedd',
        name: 'Automation Reader',
        email: 'reader@quidelortho.com',
        roles: ['LabPartner.Readers', 'LabPartner.Viewers'],
      },
    };

    const testUser = testUsers[testUserType];
    if (testUser) {
      // Create a mock account object for UserAccountService
      const account = {
        homeAccountId: `${testUser.oid}.6e63ffc0-c2fc-4cc4-b6c4-666b2ce89d92`,
        localAccountId: testUser.oid,
        username: testUser.email,
        environment: 'login.windows.net',
        tenantId: '6e63ffc0-c2fc-4cc4-b6c4-666b2ce89d92',
        name: testUser.name,
        idTokenClaims: {
          aud: '6b79bf94-8a9e-4a78-ad23-de2e7dc13ee3',
          exp: Math.floor(Date.now() / 1000) + 3600,
          name: testUser.name,
          oid: testUser.oid,
          preferred_username: testUser.email,
          roles: testUser.roles,
          tid: '6e63ffc0-c2fc-4cc4-b6c4-666b2ce89d92',
        },
      };

      this.accountService.setUser(account);
    }
  }

  setLoggedInUser() {
    // Don't execute in test mode
    if (environment.testMode) {
      console.log('[AppComponent] Skipping setLoggedInUser in test mode');
      return;
    }

    if (this.authService.instance.getAllAccounts().length > 0) {
      this.loginDisplay = true;

      const firstAccount = this.authService.instance.getAllAccounts()[0];
      console.log(`user = ${firstAccount.name}`);

      this.accountService.setUser(firstAccount);
      this.displayName = firstAccount.name!;

      // check if user has any valid roles, if app type is not ResultsBundler.
      // if not, we're outta here
      // No roles will be assigned if trying to access ResultsBundler.
      // (poor man's guard - couldn't get a real guard working with timing vs MsalGuard/login/roles)
      const roles = this.accountService.getRoles();
      if (
        (roles == undefined || roles.length == 0) &&
        this.appType !== ApplicationType.ResultsBundler
      ) {
        const props: { [key: string]: string } = { UserId: firstAccount.username };
        this.loggingService.logEvent(EVENT_USER_NO_ACCESS, props);
        this.router.navigate(['/noaccess']);
      } else {
        let props: { [key: string]: string };
        if (this.appType !== ApplicationType.ResultsBundler) {
          props = {
            UserId: firstAccount.username,
            AppVersion: this.appVersion,
            Roles: roles.join(','),
          };
        } else {
          props = {
            UserId: firstAccount.username,
            AppVersion: this.appVersion,
            Roles: '',
          };
        }
        this.loggingService.logEvent(EVENT_USER_VERSION, props);
      }
    } else {
      this.loginDisplay = false;
    }
  }

  goHome() {
    this.appState.ClearCurrentExperiment();
    if (this.appType == ApplicationType.LabPartner) {
      this.router.navigate(['/']);
    } else if (this.appType == ApplicationType.ResultsBundler) {
      this.router.navigate(['/bundles']);
    } else if (this.appType == ApplicationType.SavannaResults) {
      this.router.navigate(['/savanna-results']);
    } else if (this.appType == ApplicationType.ImageCapture) {
      this.router.navigate(['/image-capture/experiments']);
    }
  }

  showInstructionVideo() {
    const url = this.configService.getConfig().instructionVideoUrl;
    window.open(url, '_blank');
  }

  onEdit() {
    if (
      this.currentExperiment.source == undefined ||
      this.currentExperiment.source == ExperimentSource.LabPartner
    ) {
      const canEdit = this.canEditExperimentPipe.transform(
        this.currentUser,
        this.currentRoles,
        this.currentExperiment,
      );

      const dialogConfig: MatDialogConfig<ExperimentDialogData> = {
        disableClose: false,
        autoFocus: true,
        width: '60%',
        data: {
          isReadOnly: !canEdit,
          isEditMode: true,
          experiment: {
            ...this.currentExperiment,
            createdByName: this.accountService.getUserName(this.currentExperiment.createdBy),
          },
        },
      };

      const dialogRef = this.matDialogService.open<
        ExperimentDialogComponent,
        ExperimentDialogData,
        ExperimentDialogResult
      >(ExperimentDialogComponent, dialogConfig);

      dialogRef.afterClosed().subscribe((dialogResult: ExperimentDialogResult | undefined) => {
        if (dialogResult?.data) {
          this.apiService
            .getExperiment(dialogResult.data.experimentId)
            .subscribe((experiment: Experiment) => {
              this.appState.SetCurrentExperiment(experiment);
            });
        }
      });
    } else if (this.currentExperiment.source == ExperimentSource.ImageCapture) {
      const canEdit = this.canEditExperimentPipe.transform(
        this.currentUser,
        this.currentRoles,
        this.currentExperiment,
      );
      const currentExperiment = { ...this.currentExperiment };

      const dialogConfig: MatDialogConfig<ICExperimentDialogData> = {
        disableClose: false,
        autoFocus: true,
        width: '60%',
        data: {
          isReadOnly: !canEdit,
          isEditMode: true,
          experiment: {
            ...(currentExperiment as ICExperiment),
            createdByName: this.accountService.getUserName(this.currentExperiment.createdBy),
          },
        },
      };

      const dialogRef = this.matDialogService.open<
        ICExperimentDialogComponent,
        ICExperimentDialogData,
        ICExperimentDialogResult
      >(ICExperimentDialogComponent, dialogConfig);

      this.subscription.add(
        dialogRef.afterClosed().subscribe((dialogResult: ICExperimentDialogResult | undefined) => {
          if (dialogResult?.data) {
            this.apiService
              .getExperiment(dialogResult.data.experimentId)
              .subscribe((experiment: Experiment) => {
                this.appState.SetCurrentExperiment(experiment);
              });
          }
        }),
      );
    }
  }

  cloneExperiment(experiment: Experiment) {
    if (this.apiService.isLoading) {
      return;
    }

    this.apiService
      .validateSamplesAndConditions(experiment.experimentId)
      .subscribe((validateResult: boolean) => {
        if (!validateResult) {
          this.dialogService.openAckDialog(
            'Can not clone the experiment, make sure you have created samples & conditions.',
          );
        } else {
          const dialog = this.dialogService.openConfirmDialog(
            'Confirm: clone the experiment "' +
              experiment.name +
              '" (' +
              experiment.experimentId +
              ') ?',
          );
          dialog.afterClosed().subscribe(dialogResult => {
            if (dialogResult) {
              this.apiService.cloneExperiment(experiment.experimentId).subscribe(
                (clonedExperiment: Experiment) => {
                  if (clonedExperiment) {
                    this.notificationService.success('Experiment cloned successfully');

                    const props: { [key: string]: number } = {
                      ExperimentId: clonedExperiment.experimentId,
                      DeviceType: experiment.deviceType,
                    };
                    this.loggingService.logEvent(EVENT_CLONE_EXPERIMENT, props);

                    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                      this.router.navigate([
                        '/experiment-detail/' + clonedExperiment.experimentId + '/0',
                      ]);
                    });
                  } else {
                    this.notificationService.error('Error cloning experiment');
                  }
                },
                () => {
                  this.notificationService.error('Error cloning experiment');
                },
              );
            } else {
              this.notificationService.warn(':: Canceled');
            }
          });
        }
      });
  }

  showAuditDialog(experiment: Experiment) {
    this.subscription.add(
      this.dialogService.openChangelogDialog(
        `Change log for ${experiment.name} (${this.experimentPipe.transform(
          experiment.experimentId,
          experiment.type,
        )})`,
        experiment.experimentId,
        AuditObjectName.Experiment,
        experiment.experimentId,
      ),
    );
  }

  showReleaseNotes() {
    this.matDialogService.open(ReleaseNotesDialogComponent, {
      width: '800px',
      panelClass: 'release-notes-dialog-container',
      disableClose: false,
      position: { top: '10px' },
      autoFocus: false,
    });
  }

  showContactAndSupport() {
    this.matDialogService.open(ContactAndSupportDialogComponent, {
      width: '800px',
      panelClass: 'release-notes-dialog-container',
      disableClose: false,
      position: { top: '10px' },
      autoFocus: false,
    });
  }

  navigateBundlesRoute(): void {
    this.router.navigate(['/bundles']).then(() => {
      this.titleBarName = 'Results Bundler';
      this.appType = ApplicationType.ResultsBundler;
      this.isTitlebarToolTipDisabled = true;
      this.appState.ClearCurrentExperiment();
    });
  }

  navigateLabpartnerRoute(): void {
    this.router.navigate(['/']).then(() => {
      this.titleBarName = 'Lab Partner';
      this.appType = ApplicationType.LabPartner;
      this.isTitlebarToolTipDisabled = false;
      this.appState.ClearCurrentExperiment();
    });
  }

  navigateSavannaRoute(): void {
    this.router.navigate(['/savanna-results']).then(() => {
      this.titleBarName = 'Savanna Results';
      this.appType = ApplicationType.SavannaResults;
      this.isTitlebarToolTipDisabled = true;
      this.appState.ClearCurrentExperiment();
    });
  }

  navigateAdmin(): void {
    this.router.navigate(['/admin/assays']).then(() => {
      this.titleBarName = 'Manage Assays';
      this.appType = ApplicationType.LabPartner;
      this.isTitlebarToolTipDisabled = false;
      this.appState.ClearCurrentExperiment();
    });
  }

  navigateImageCapture(): void {
    this.router.navigate(['/image-capture/experiments']).then(() => {
      this.titleBarName = 'Image Capture';
      this.appType = ApplicationType.ImageCapture;
      this.isTitlebarToolTipDisabled = false;
      this.appState.ClearCurrentExperiment();
    });
  }

  getBannerClass(): string {
    if (this.isProduction) {
      return '';
    }
    return this.environmentName.toLowerCase() === 'validated'
      ? 'banner-validated'
      : 'banner-non-prod';
  }

  getRounds(roundsNumber?: number): number[] {
    if (roundsNumber === undefined || roundsNumber < 1) {
      roundsNumber = 1;
    }
    const rounds: number[] = [];
    for (let i = 1; i <= roundsNumber; i++) {
      rounds.push(i);
    }
    return rounds;
  }

  getICExperiment(): ICExperiment | null {
    return this.currentExperiment as ICExperiment;
  }

  onChangeRound(event: any): void {
    this.icRoundService.setCurrentRound(event.target.value);
  }

  showTestLoginDialog(): void {
    const dialogRef = this.matDialogService.open(TestLoginDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        testUsers: this.testUserCatalog.getTestUsers(),
        currentUser: this.testUserCatalog.getCurrentUser(),
      },
    });

    dialogRef.afterClosed().subscribe(async (result: any) => {
      if (result && result.success && result.user) {
        try {
          console.log('Test user logged in:', result.user);

          // Call TestToken endpoint to get JWT token for local test mode
          if (environment.testMode) {
            await this.testLoginService.loginWithTestToken(result.user);
          }

          // Set the selected user in the catalog
          this.testUserCatalog.setCurrentUser(result.user);
          this.displayName = result.user.name;
          this.loginDisplay = true; // Update the UI to show logged in state

          // Update the UserAccountService with the new user
          this.setTestUserInAccountService(result.user.id);

          // Redirect to home page after successful login
          this.router.navigate(['/']);
        } catch (error) {
          console.error('[AppComponent] Error logging in test user:', error);
        }
      } else {
        // If user cancelled, just close the dialog (don't show it again)
        console.log('Test login cancelled by user');
      }
    });
  }
}
